/**
 * Lists what the programme still needs for each readiness level.
 *
 * The governance surface decides whether a transition is permitted; this says
 * what is missing and where to fix it, which is the submission pack's own question.
 *
 * @module components/submission/ReadinessChecklist
 */

import React from "react";

import { groupBlockersByAction } from "@/components/shared/readiness-groups";
import { ReadinessBadge } from "@/components/shared/ReadinessBadge";
import {
  type ReadinessBlockerEntry,
  ReadinessBlockerList,
} from "@/components/shared/ReadinessBlockerList";
import { Accordion, AccordionItem, Badge, countLabel, MutedText, Panel } from "@/components/ui";
import { meetsReadinessGate, READINESS_RANK, READINESS_RUNGS } from "@/server/readiness-ladder";
import type { ReadinessBlocker, ReadinessResult, ReadinessState } from "@/server/types";

/** The rungs a reader can act on. The first is the floor, the last the end. */
const GATES: readonly ReadinessState[] = READINESS_RUNGS.slice(1, -1);

/**
 * The checks a rung adds: each sits under the first rung that needs it, so a
 * check is listed once however many rungs above it also wait on it.
 */
function addedByGate(blockers: readonly ReadinessBlocker[], index: number): ReadinessBlocker[] {
  const floor = index === 0 ? -1 : READINESS_RANK[GATES[index - 1]!];
  const rank = READINESS_RANK[GATES[index]!];
  return blockers.filter(
    (blocker) => READINESS_RANK[blocker.gates] > floor && READINESS_RANK[blocker.gates] <= rank,
  );
}

export interface ReadinessChecklistProps {
  readiness: ReadinessResult;
  /**
   * Where a blocker is fixed: the section's name, its address, or why it is
   * closed. The section keys are the editor's, which only the router resolves.
   */
  destinationFor?: (
    blocker: ReadinessBlocker,
  ) => Pick<ReadinessBlockerEntry, "section" | "href" | "lockedReason">;
}

function GateBadge({ met, open }: { met: boolean; open: number }): React.ReactElement {
  if (met) {
    return (
      <Badge tone="success" variant="soft">
        Done
      </Badge>
    );
  }
  if (open === 0) {
    return (
      <Badge tone="neutral" variant="soft">
        Waiting on the previous checks
      </Badge>
    );
  }
  return (
    <Badge tone="warning" variant="soft">
      {countLabel(open, "check")} open
    </Badge>
  );
}

export function ReadinessChecklist({
  readiness,
  destinationFor = undefined,
}: ReadinessChecklistProps): React.ReactElement {
  const firstUnmet = GATES.find((gate) => !meetsReadinessGate(readiness.state, gate));

  return (
    <Panel
      title="Export gates"
      icon="checks"
      description="What the programme needs to reach each level. Each level also needs everything above it, and the submission pack exports once the last is met."
      data-testid="submission-readiness"
      actions={<ReadinessBadge state={readiness.state} data-testid="submission-readiness-state" />}
    >
      <Accordion
        id="submission-readiness-gates"
        defaultExpandedKeys={firstUnmet ? [firstUnmet] : []}
      >
        {GATES.map((gate, index) => {
          const met = meetsReadinessGate(readiness.state, gate);
          const blockers = met ? [] : addedByGate(readiness.blockers, index);
          return (
            <AccordionItem
              key={gate}
              eventKey={gate}
              headingLevel={3}
              title={gate}
              data-testid={`readiness-gate-${gate.toLowerCase().replace(/\s+/g, "-")}`}
              headerActions={<GateBadge met={met} open={blockers.length} />}
            >
              {blockers.length === 0 ? (
                <MutedText>
                  {met
                    ? "Nothing left to complete at this level."
                    : "Nothing more than the levels above need."}
                </MutedText>
              ) : (
                <ReadinessBlockerList
                  entries={groupBlockersByAction(blockers).map((group) => ({
                    key: group.key,
                    id: group.requirement,
                    message: group.label,
                    // The row keeps a way in: the first module's, beside the rest.
                    ...destinationFor?.(group.blockers[0]),
                    ...(group.moduleCount >= 2
                      ? {
                          count: group.moduleCount,
                          details: group.blockers.map((blocker) => ({
                            key: `${blocker.target?.moduleId ?? ""}:${blocker.message}`,
                            message: blocker.message,
                            ...destinationFor?.(blocker),
                          })),
                        }
                      : {}),
                  }))}
                />
              )}
            </AccordionItem>
          );
        })}
      </Accordion>
    </Panel>
  );
}

export default ReadinessChecklist;
